import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import FocusTrap from 'focus-trap-react';

import { PrsOutlineButtonMd, PrsDangerButtonMd } from '../../styles-and-themes/styles/prsButtons';
import {
  PrsDialog,
  PrsDialogBox,
  PrsDialogBody,
  PrsDialogHeading,
  PrsDialogText,
  PrsDialogAction,
} from './MfaCancelDialogStyles';

/**
 * Confirmation dialog shown when the user attempts to abandon a 2FA flow before
 * completing it. "Go Back" returns to the in-progress 2FA step; "Cancel" calls
 * onConfirm, which the parent uses to actually leave (route change, panel back, etc.).
 *
 * Esc and backdrop click both behave as "Go Back" — the destructive choice
 * requires an explicit click on the red Cancel button.
 */
const MfaCancelDialog = ({
  isOpen,
  onConfirm,
  onDismiss,
  heading,
  body,
  goBackLabel,
  cancelLabel,
}) => {
  const goBackRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const prevOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    return () => { document.documentElement.style.overflow = prevOverflow; };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <FocusTrap
      focusTrapOptions={{
        initialFocus: () => goBackRef.current,
        escapeDeactivates: () => { onDismiss(); return false; },
        clickOutsideDeactivates: false,
      }}
    >
      <PrsDialog onMouseDown={(e) => { if (e.target === e.currentTarget) onDismiss(); }}>
        <PrsDialogBox role="alertdialog" aria-modal="true" aria-labelledby="mfa-cancel-heading" aria-describedby="mfa-cancel-body">
          <PrsDialogBody>
            <PrsDialogHeading id="mfa-cancel-heading">{heading}</PrsDialogHeading>
            <PrsDialogText id="mfa-cancel-body">{body}</PrsDialogText>
          </PrsDialogBody>
          <PrsDialogAction>
            <PrsOutlineButtonMd type="button" onClick={onDismiss} ref={goBackRef}>
              {goBackLabel}
            </PrsOutlineButtonMd>
            <PrsDangerButtonMd type="button" onClick={onConfirm}>
              {cancelLabel}
            </PrsDangerButtonMd>
          </PrsDialogAction>
        </PrsDialogBox>
      </PrsDialog>
    </FocusTrap>
  );
};

MfaCancelDialog.defaultProps = {
  heading: 'Are you sure you want to cancel?',
  body: "You haven't completed your two-factor authentication. If you exit now, your changes won't be saved.",
  goBackLabel: 'Go Back',
  cancelLabel: 'Cancel',
};

MfaCancelDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
  heading: PropTypes.string,
  body: PropTypes.string,
  goBackLabel: PropTypes.string,
  cancelLabel: PropTypes.string,
};

export default MfaCancelDialog;
